import React, { useState } from 'react'
import { BaseRouteParams, withNavigation, WithNavigationProps } from 'hocs'
import { usePermission } from 'admin/hocs'
import { DatePicker } from 'uiComponents/popups/datePickerInput'
import { ConfirmationDialog } from 'uiComponents/popups/confirmationDialog'
import { useUpdateVisitDateAndTimeMutation } from 'orders/reduxQueries'
import { useDispatch } from 'react-redux'
import { addMessage } from 'uiComponents/messages/actions'
import { IOrder } from 'orders/schema'
import { DateFormats, formatOrInvalid, parseDate } from 'utils/dates'
import { useTranslation } from 'translations'

interface InlineVisitDateProps extends WithNavigationProps<BaseRouteParams> {
    order: IOrder
    onUpdated?: (order: IOrder) => void
}

function InlineVisitDate({ order, match, onUpdated }: InlineVisitDateProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [date, setDate] = useState<Date>()
    const { accountSlug } = match.params
    const [updateDateTime] = useUpdateVisitDateAndTimeMutation()
    const dispatch = useDispatch()
    const { hasPermission } = usePermission()
    
    const canEditOrders =
        hasPermission('edit_orders', accountSlug) && order.status !== 'refunded' && order.status !== 'refunding'

    const close = () => {
        setDate(undefined)
        setOpen(false)
    }

    const saveDate = async () => {
        try {
            await updateDateTime({
                orderUuid: order.id,
                dateTime: formatOrInvalid(date as Date, 'yyyy-MM-dd'),
                visitTime: null,
            }).unwrap()
            if (onUpdated) {
                onUpdated({ ...order, visitDate: formatOrInvalid(date as Date, 'yyyy-MM-dd') })
            }
        } catch (e) {
            if (e.message === 'no_availability') {
                dispatch(
                    addMessage('no_availability_error', 'error', t('orders.orders.inlineVisitDate.noAvailability')),
                )
            } else if (e.message === 'order_already_redeemed') {
                dispatch(
                    addMessage('already_redeemed_error', 'error', t('orders.orders.inlineVisitDate.alreadyRedeemed')),
                )
            } else if (e.message === 'date_change_not_possible') {
                dispatch(
                    addMessage(
                        'date_change_not_possible',
                        'error',
                        t('orders.orders.inlineVisitDate.changeNotPossible'),
                    ),
                )
            } else {
                dispatch(addMessage('visit_date_error', 'error', t('orders.orders.inlineVisitDate.saveError')))
            }
        }
        close()
    }

    if (!order.visitDate) {
        return <>{t('orders.orders.inlineVisitDate.undated')}</>
    }

    return (
        <>
            {!!open && (
                <ConfirmationDialog
                    title={t('orders.orders.inlineVisitDate.confirmTitle')}
                    text={t('orders.orders.inlineVisitDate.confirmText', {
                        date: formatOrInvalid(date as Date, DateFormats.EXTRA_LONG_DATE),
                    })}
                    buttonText={t('orders.orders.inlineVisitDate.confirmButton')}
                    onCancel={close}
                    onConfirm={saveDate}
                />
            )}
            <DatePicker
                date={order.visitDate ? parseDate(order.visitDate) : null}
                inlineEdit
                modalDatePicker
                onInlineEditAccept={(newDate) => {
                    setDate(newDate)
                    setOpen(true)
                }}
                disabled={!canEditOrders}
                inlineEditDateFormat={DateFormats.LONG_DATE}
            />
        </>
    )
}

export default withNavigation(InlineVisitDate)
